import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { PublicUser } from '@bn/shared';
import { ApiError, api } from './api';

interface MeResponse {
  user: PublicUser;
  mustChangePassword: boolean;
}

interface AuthValue {
  user: PublicUser | null;
  mustChangePassword: boolean;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PublicUser | null>(null);
  const [mustChangePassword, setMustChange] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const me = await api.get<MeResponse>('/api/auth/me');
      setUser(me.user);
      setMustChange(me.mustChangePassword);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setUser(null);
        setMustChange(false);
      } else {
        throw error;
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (username: string, password: string) => {
    const me = await api.post<MeResponse>('/api/auth/login', { username, password });
    setUser(me.user);
    setMustChange(me.mustChangePassword);
  }, []);

  const logout = useCallback(async () => {
    await api.post('/api/auth/logout');
    setUser(null);
    setMustChange(false);
  }, []);

  const value = useMemo<AuthValue>(
    () => ({ user, mustChangePassword, loading, login, logout, refresh }),
    [user, mustChangePassword, loading, login, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth doit être utilisé dans <AuthProvider>');
  return value;
}
